// src/components/QueryInput.js
import React from 'react';
import {
  Box,
  InputBase,
  IconButton,
  Tooltip,
} from '@mui/material';
// import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
// import SettingsIcon from '@mui/icons-material/Settings';
import SubmitIcon from '../assets/submit-icon.svg';
import WhiteSquareIcon from '../assets/white-square.svg';
import { useLocation } from 'react-router-dom';
// import HelpFeedbackMenu from './HelpFeedbackMenu';

function QueryInput({
  value,
  onChange,
  onSend,
  compact = false,
  isStreaming = false,
  onCancelStreaming,
  isStartLoading = false,
  selectedAgent = 'auto',
  onAgentChange,
  selectedTone = 'professional',
  onToneChange,
}) {

  const isHomePage = useLocation().pathname === '/';

  // When user presses Enter inside the input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isStreaming && !isStartLoading) {
      e.preventDefault();
      onSend();
    }
  };

  // Called when the user clicks the arrow button
  const handleSendClick = () => {
    if (!isStreaming && !isStartLoading) onSend();
    else if (isStreaming && onCancelStreaming) onCancelStreaming({ source: 'user_cancel' });
  };

  // const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  // const handleOpenHelp = (e) => setHelpAnchorEl(e.currentTarget);
  // const handleCloseHelp = () => setHelpAnchorEl(null);

  return (
    <Box
      sx={{
        maxWidth: '100%',
        overflow: 'visible',
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: compact ? '12px' : 0,
      }}
    >
      {/* Main rounded container with single row */}
      <Box
        sx={{
          width: isHomePage ? '100%' : compact ? '100%' : 800,
          maxWidth: '100%',
          minHeight: compact ? 40 : 40,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          borderRadius: compact ? '12px' : '20px',
          background: 'rgba(255, 255, 255, 0.4)',
          border: '0.8px solid rgba(200, 200, 200, 0.7)',
          boxShadow: '0 5px 8px rgba(0, 0, 0, 0.06)',
          px: compact ? 1.5 : 1.5,
          py: compact ? 0.5 : 0.5,
          gap: 1,
        }}
      >
        {/* Text input on the left */}
        <InputBase
          multiline
          maxRows={3}
          sx={{
            flex: 1,
            fontSize: compact ? '14px' : '16px',
            maxHeight: '72px',

            '& .MuiInputBase-inputMultiline': {
              padding: 0,
              overflowY: 'auto',

              /* WebKit scrollbar */
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#bfbfbf',
                borderRadius: '2px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#a6a6a6',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },

              /* Firefox */
              scrollbarWidth: 'thin',
              scrollbarColor: '#bfbfbf transparent',
            },
          }}
          placeholder={isStreaming ? '' : 'Type your query here...'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
        />

        {/* Submit/Stop button on the right */}
        {isStreaming ? (
          <Tooltip
            title="Exit stream and view results later"
            arrow={false}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'rgba(255, 255, 255, 0.65)',
                  color: '#000',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  fontSize: '14px',
                  border: '1px solid rgba(200, 200, 200, 0.6)',
                  borderRadius: '10px',
                  padding: '8px 12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
              },
            }}
          >
            <IconButton
              onClick={handleSendClick}
              disabled={false}
              sx={{
                cursor: 'pointer',
                pr: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                },
              }}
            >
              <Box
                component="img"
                src={WhiteSquareIcon}
                alt="Exit stream"
                sx={{
                  width: 32,
                  height: 32,
                  '&:hover': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s ease-in-out',
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton
            onClick={handleSendClick}
            disabled={isStartLoading}
            sx={{
              cursor: 'pointer',
              pr: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
            }}
          >
            <Box
              component="img"
              src={SubmitIcon}
              alt="Submit"
              sx={{
                width: 32,
                height: 32,
                opacity: isStartLoading ? 0.4 : 1,
                '&:hover': {
                  transform: 'scale(1.05)',
                  transition: 'transform 0.2s ease-in-out',
                },
              }}
            />
          </IconButton>
        )}
      </Box>

      {/*
        OLD TWO-ROW LAYOUT - Kept for reference if needed

        The old layout had:
        - Top row with text input
        - Bottom row with agent selector dropdown, tone selector dropdown, v3 label, and submit button

        If you need to restore it, check git history or uncomment the code below.
      */}

      {/* Help / Settings icons commented out for now */}
      {/* <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <Tooltip title="Help & Feedback" arrow={false} componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'rgba(255, 255, 255, 0.65)',
              color: '#000',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              fontSize: '14px',
              border: '1px solid rgba(200, 200, 200, 0.6)',
              borderRadius: '10px',
              padding: '8px 12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            },
          },
        }}>
          <IconButton aria-label="Help" sx={{ mr: 2, cursor: 'pointer' }} onClick={handleOpenHelp}>
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
        <HelpFeedbackMenu anchorEl={helpAnchorEl} onClose={handleCloseHelp} />
        <Tooltip title="Customize your experience. Settings page coming soon!" arrow={false} componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'rgba(255, 255, 255, 0.65)',
              color: '#000',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              fontSize: '14px',
              border: '1px solid rgba(200, 200, 200, 0.6)',
              borderRadius: '10px',
              padding: '8px 12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            },
          },
        }}>
          <IconButton aria-label="Settings" sx={{ cursor: 'pointer' }}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Box> */}
    </Box>
  );
}

export default QueryInput;
